import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import QRCode from "react-qr-code";
import { PDFDocument, StandardFonts, rgb, PageSizes, PDFPage, PDFFont, PDFString } from "pdf-lib";
import JSZip from "jszip";
import { CreateCreditVouchers } from "../types";
import { DateTime } from "luxon";
import { formatCurrency, generateVoucherDeeplinkUrl } from "src/utils/util";

export type DownloadVouchersCodesPdf = {
  codes: string[];
  vouchersInfo: CreateCreditVouchers & {
    organizationName?: string;
    projectName?: string;
  };
};

const COLORS = {
  primary: rgb(0.1, 0.1, 0.1), // Near black
  secondary: rgb(0.5, 0.5, 0.5), // Mid gray
  light: rgb(0.7, 0.7, 0.7), // Light gray
  accent: rgb(0.2, 0.4, 0.7), // Professional blue
  background: rgb(0.99, 0.99, 0.99), // Almost white
};

const VOUCHER_LINK_TEXT = "Apply voucher code";
const SEPARATE_PDF_COLORS = {
  primary: rgb(43 / 255, 0, 212 / 255),
  text: rgb(17 / 255, 17 / 255, 17 / 255),
  muted: rgb(102 / 255, 102 / 255, 102 / 255),
  border: rgb(216 / 255, 220 / 255, 239 / 255),
  white: rgb(1, 1, 1),
};

function formatDate(input: Date | string): string {
  const d = typeof input === "string" ? new Date(input) : input;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function fitTextSize(text: string, font: PDFFont, maxFontSize: number, minFontSize: number, maxWidth: number): number {
  let fontSize = maxFontSize;

  while (fontSize > minFontSize && font.widthOfTextAtSize(text, fontSize) > maxWidth) {
    fontSize -= 0.25;
  }

  return fontSize;
}

function addLinkAnnotation(page: PDFPage, doc: PDFDocument, uri: string, rect: [number, number, number, number]) {
  const linkAnnotation = doc.context.obj({
    Type: "Annot",
    Subtype: "Link",
    Rect: rect,
    Border: [0, 0, 0],
    A: {
      Type: "Action",
      S: "URI",
      URI: PDFString.of(uri),
    },
  });

  const linkAnnotationRef = doc.context.register(linkAnnotation);
  page.node.addAnnot(linkAnnotationRef);
}

async function drawHeader(page: PDFPage, doc: PDFDocument, y: number): Promise<number> {
  const { width } = page.getSize();
  // const boldFont = await doc.embedFont(StandardFonts.HelveticaBold);
  const regularFont = await doc.embedFont(StandardFonts.Helvetica);

  // Document type - clean subtitle
  page.drawText("Voucher Codes", {
    x: 60,
    y: y - 100,
    size: 16,
    font: regularFont,
    color: COLORS.secondary,
  });

  // Date - top right
  page.drawText(formatDate(new Date()), {
    x: width - 120,
    y: y - 100,
    size: 12,
    font: regularFont,
    color: COLORS.light,
  });

  return y - 130; // 100 padding + 30 for the heigh of the element
}

async function drawSummaryBar(page: PDFPage, doc: PDFDocument, vouchersInfo: any, y: number) {
  const { width } = page.getSize();
  const boldFont = await doc.embedFont(StandardFonts.HelveticaBold);

  // Clean background bar
  page.drawRectangle({
    x: 60,
    y: y - 35,
    width: width - 120,
    height: 35,
    color: COLORS.background,
  });

  // Key metrics in one clean line
  const totalValue = formatCurrency(vouchersInfo.amount * vouchersInfo.count, vouchersInfo.currency);
  const summaryText = `${vouchersInfo.count} vouchers • ${formatCurrency(
    vouchersInfo.amount,
    vouchersInfo.currency
  )} each • ${totalValue} total`;

  page.drawText(summaryText, {
    x: 80,
    y: y - 22,
    size: 14,
    font: boldFont,
    color: COLORS.accent,
  });

  return y - 70; // 35 padding + 35 heigh;
}

async function drawInfoGrid(
  page: PDFPage,
  doc: PDFDocument,
  vouchersInfo: DownloadVouchersCodesPdf["vouchersInfo"],
  y: number
): Promise<number> {
  const regularFont = await doc.embedFont(StandardFonts.Helvetica);
  const { width } = page.getSize();

  // fixed X-positions for left/right columns
  const leftX = 80;
  const rightX = width / 2 + 40;

  // how much vertical space each row takes
  const rowSpacing = 42;
  const startY = y;

  // your existing data
  const infoPairs: [string, string][] = [
    ["Type", vouchersInfo.type],
    ["Currency", vouchersInfo.currency],
    ["Voucher Amount", vouchersInfo.amount.toString()],
    ["Expires", formatDate(vouchersInfo.expiryDate || getCreditVoucherExpiryDate())],
    ["Organization", vouchersInfo.organizationName || "—"],
    ["Project", vouchersInfo.projectName || "—"],
  ];

  // draw each label/value
  infoPairs.forEach((pair, idx) => {
    const colX = idx % 2 === 0 ? leftX : rightX;
    const row = Math.floor(idx / 2);
    // Y of the label
    const labelY = startY - row * rowSpacing;
    // Y of the value (a little below the label)
    const valueY = labelY - 14;

    // label (smaller, light color)
    page.drawText(pair[0], {
      x: colX,
      y: labelY,
      size: 10,
      font: regularFont,
      color: COLORS.light,
    });

    // value (bigger, dark color)
    page.drawText(pair[1], {
      x: colX,
      y: valueY,
      size: 12,
      font: regularFont,
      color: COLORS.primary,
    });
  });

  // how many rows did we actually use?
  const usedRows = Math.ceil(infoPairs.length / 2);

  // return a Y that accounts for all rows + a little bottom buffer
  return startY - usedRows * rowSpacing - 30;
}

async function drawLine(page: PDFPage, y: number) {
  const { width } = page.getSize();
  let currentY = y;
  // Section divider
  page.drawLine({
    start: { x: 60, y: currentY + 40 },
    end: { x: width - 60, y: currentY + 40 },
    thickness: 0.5,
    color: COLORS.light,
  });
}

async function qrValueToPngBytes(value: string, size: number): Promise<Uint8Array> {
  const svgMarkup = renderToStaticMarkup(
    React.createElement(QRCode, {
      value,
      size,
      bgColor: "#FFFFFF",
      fgColor: "#111111",
    })
  );

  const svgBlob = new Blob([svgMarkup], {
    type: "image/svg+xml;charset=utf-8",
  });
  const objectUrl = URL.createObjectURL(svgBlob);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("Failed to render voucher QR code"));
      img.src = objectUrl;
    });

    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Failed to create canvas context for voucher QR code");
    }

    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, size, size);
    context.drawImage(image, 0, 0, size, size);

    const pngBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to export voucher QR code image"));
        }
      }, "image/png");
    });

    return new Uint8Array(await pngBlob.arrayBuffer());
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function roundedRectanglePath(width: number, height: number, radius: number): string {
  return [
    `M ${radius} 0`,
    `H ${width - radius}`,
    `Q ${width} 0 ${width} ${radius}`,
    `V ${height - radius}`,
    `Q ${width} ${height} ${width - radius} ${height}`,
    `H ${radius}`,
    `Q 0 ${height} 0 ${height - radius}`,
    `V ${radius}`,
    `Q 0 0 ${radius} 0`,
    "Z",
  ].join(" ");
}

function downloadBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

async function createSeparateVoucherPdf({
  code,
  index,
  total,
  vouchersInfo,
}: {
  code: string;
  index: number;
  total: number;
  vouchersInfo: DownloadVouchersCodesPdf["vouchersInfo"];
}): Promise<Uint8Array> {
  const doc = await PDFDocument.create();
  const page = doc.addPage(PageSizes.A5);
  const { width, height } = page.getSize();
  const regularFont = await doc.embedFont(StandardFonts.Helvetica);
  const boldFont = await doc.embedFont(StandardFonts.HelveticaBold);
  const codeFont = await doc.embedFont(StandardFonts.HelveticaBold);
  const voucherUrl = generateVoucherDeeplinkUrl(code);

  const margin = 50;
  const cardX = margin;
  const cardY = 96;
  const cardWidth = width - margin * 2;
  const cardHeight = 380;
  const headerOverlap = 24;
  const headerHeight = height - (cardY + cardHeight) + headerOverlap;

  page.drawRectangle({
    x: 0,
    y: height - headerHeight,
    width,
    height: headerHeight,
    color: SEPARATE_PDF_COLORS.primary,
  });

  page.drawText("Taaly Voucher", {
    x: margin,
    y: height - 62,
    size: 18,
    font: boldFont,
    color: SEPARATE_PDF_COLORS.white,
  });

  const cardPath = roundedRectanglePath(cardWidth, cardHeight, 12);

  page.drawSvgPath(cardPath, {
    x: cardX + 2,
    y: cardY + cardHeight - 3,
    color: rgb(0, 0, 0),
    opacity: 0.08,
  });

  page.drawSvgPath(cardPath, {
    x: cardX,
    y: cardY + cardHeight,
    color: SEPARATE_PDF_COLORS.white,
    borderColor: SEPARATE_PDF_COLORS.border,
    borderWidth: 1,
  });

  const contentX = cardX + 30;
  let y = cardY + cardHeight - 28;

  page.drawText(`Voucher ${String(index + 1).padStart(2, "0")} of ${total}`, {
    x: contentX,
    y,
    size: 9,
    font: regularFont,
    color: SEPARATE_PDF_COLORS.muted,
  });

  y -= 42;
  page.drawText("Voucher Code", {
    x: contentX,
    y,
    size: 12,
    font: boldFont,
    color: SEPARATE_PDF_COLORS.text,
  });

  y -= 28;
  const codeFontSize = fitTextSize(code, codeFont, 18, 12, cardWidth - 60);
  page.drawText(code, {
    x: contentX,
    y,
    size: codeFontSize,
    font: codeFont,
    color: SEPARATE_PDF_COLORS.primary,
  });

  const qrSize = 130;
  const qrY = y - qrSize - 18;
  const qrImageBytes = await qrValueToPngBytes(voucherUrl, qrSize * 2);
  const qrImage = await doc.embedPng(qrImageBytes);
  page.drawImage(qrImage, {
    x: contentX,
    y: qrY,
    width: qrSize,
    height: qrSize,
  });

  const linkY = qrY - 28;
  const linkFontSize = 11;
  const linkWidth = boldFont.widthOfTextAtSize(VOUCHER_LINK_TEXT, linkFontSize);

  page.drawText(VOUCHER_LINK_TEXT, {
    x: contentX,
    y: linkY,
    size: linkFontSize,
    font: boldFont,
    color: SEPARATE_PDF_COLORS.primary,
  });
  page.drawLine({
    start: { x: contentX, y: linkY - 2 },
    end: { x: contentX + linkWidth, y: linkY - 2 },
    thickness: 0.5,
    color: SEPARATE_PDF_COLORS.primary,
  });
  addLinkAnnotation(page, doc, voucherUrl, [contentX, linkY - 4, contentX + linkWidth, linkY + 12]);

  const details: [string, string][] = [
    ["Type", vouchersInfo.type],
    ["Organization", vouchersInfo.organizationName || "-"],
    ["Project", vouchersInfo.projectName || "-"],
  ];

  y = linkY - 34;
  details.forEach(([label, value]) => {
    page.drawText(label, {
      x: contentX,
      y,
      size: 10,
      font: boldFont,
      color: SEPARATE_PDF_COLORS.text,
    });

    const valueX = contentX + 82;
    const valueFontSize = fitTextSize(value, regularFont, 10, 7, cardX + cardWidth - valueX - 20);
    page.drawText(value, {
      x: valueX,
      y,
      size: valueFontSize,
      font: regularFont,
      color: SEPARATE_PDF_COLORS.text,
    });

    y -= 24;
  });

  return doc.save();
}

export async function downloadVouchersCodesZip({ codes, vouchersInfo }: DownloadVouchersCodesPdf) {
  const zip = new JSZip();

  for (let index = 0; index < codes.length; index++) {
    const code = codes[index];
    const pdfBytes = await createSeparateVoucherPdf({
      code,
      index,
      total: codes.length,
      vouchersInfo,
    });
    zip.file(`${String(index + 1).padStart(2, "0")}-${sanitizeFilenamePart(code)}.pdf`, pdfBytes);
  }

  const timestamp = DateTime.now().toFormat("yyyy-LLL-dd");
  const organizationSuffix = vouchersInfo.organizationName
    ? `-${sanitizeFilenamePart(vouchersInfo.organizationName)}`
    : "";
  const filename = `vouchers-${vouchersInfo.type.toLowerCase()}${organizationSuffix}-${timestamp}-separate.zip`;
  const zipBlob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });

  downloadBlob(zipBlob, filename);
}

function getCodesPerPage(startY: number, pageHeight: number): number {
  const cardHeight = 112;
  const rowGap = 12;
  const footerSpace = 60;
  const availableHeight = Math.max(startY - footerSpace, 0);
  const rows = Math.max(1, Math.floor((availableHeight + rowGap) / (cardHeight + rowGap)));
  return rows * 2;
}

function calculateTotalPages(codesCount: number, firstPageCapacity: number, additionalPageCapacity: number): number {
  if (codesCount <= firstPageCapacity) return 1;

  const remainingCodes = codesCount - firstPageCapacity;
  return 1 + Math.ceil(remainingCodes / additionalPageCapacity);
}

async function drawCodesList(page: PDFPage, doc: PDFDocument, codes: string[], y: number, startIndex: number = 0) {
  const codeFont = await doc.embedFont(StandardFonts.Courier);
  const regularFont = await doc.embedFont(StandardFonts.Helvetica);
  const boldFont = await doc.embedFont(StandardFonts.HelveticaBold);
  const { width } = page.getSize();
  const cardWidth = (width - 136) / 2;
  const cardHeight = 112;
  const qrSize = 64;
  const rowGap = 12;
  const qrPadding = 10;
  const textXOffset = 14;
  const textMaxWidth = cardWidth - qrSize - qrPadding - textXOffset - 20;

  for (let index = 0; index < codes.length; index++) {
    const code = codes[index];
    const voucherUrl = generateVoucherDeeplinkUrl(code);
    const number = startIndex + index + 1;
    const row = Math.floor(index / 2);
    const column = index % 2;
    const x = 60 + column * (cardWidth + 16);
    const topY = y - row * (cardHeight + rowGap);
    const qrImageBytes = await qrValueToPngBytes(voucherUrl, qrSize * 2);
    const qrImage = await doc.embedPng(qrImageBytes);

    page.drawRectangle({
      x,
      y: topY - cardHeight,
      width: cardWidth,
      height: cardHeight,
      color: column === 0 ? COLORS.background : rgb(1, 1, 1),
      borderColor: rgb(0.88, 0.9, 0.94),
      borderWidth: 0.8,
    });

    page.drawText(number.toString().padStart(2, "0"), {
      x: x + textXOffset,
      y: topY - 18,
      size: 10,
      font: boldFont,
      color: COLORS.accent,
    });

    page.drawText("Voucher Code", {
      x: x + textXOffset,
      y: topY - 34,
      size: 9,
      font: regularFont,
      color: COLORS.light,
    });

    page.drawText(code, {
      x: x + textXOffset,
      y: topY - 52,
      size: 11,
      font: codeFont,
      color: COLORS.primary,
    });

    page.drawText("Auto-fill link", {
      x: x + textXOffset,
      y: topY - 74,
      size: 8,
      font: regularFont,
      color: COLORS.light,
    });

    const linkFontSize = fitTextSize(VOUCHER_LINK_TEXT, regularFont, 8, 6, textMaxWidth);

    page.drawText(VOUCHER_LINK_TEXT, {
      x: x + textXOffset,
      y: topY - 89,
      size: linkFontSize,
      font: regularFont,
      color: COLORS.accent,
    });

    addLinkAnnotation(page, doc, voucherUrl, [
      x + textXOffset,
      topY - 91,
      x + textXOffset + regularFont.widthOfTextAtSize(VOUCHER_LINK_TEXT, linkFontSize),
      topY - 80,
    ]);

    page.drawImage(qrImage, {
      x: x + cardWidth - qrSize - qrPadding,
      y: topY - qrSize - 16,
      width: qrSize,
      height: qrSize,
    });
  }

  const totalRows = Math.ceil(codes.length / 2);
  return y - totalRows * (cardHeight + rowGap);
}

async function drawFooter(page: PDFPage, doc: PDFDocument, pageNum: number, totalPages: number) {
  const { width } = page.getSize();
  const font = await doc.embedFont(StandardFonts.Helvetica);

  // Simple page indicator
  if (totalPages > 1) {
    page.drawText(`${pageNum} of ${totalPages}`, {
      x: width - 100,
      y: 40,
      size: 10,
      font,
      color: COLORS.light,
    });
  }
}

export async function downloadVouchersCodesPdf({ codes, vouchersInfo }: DownloadVouchersCodesPdf) {
  const doc = await PDFDocument.create();

  // First page
  let page = doc.addPage(PageSizes.A4);
  let pageNum = 1;

  // Header
  let currentY = await drawHeader(page, doc, page.getHeight());

  // Summary bar
  currentY = await drawSummaryBar(page, doc, vouchersInfo, currentY);

  // Info grid
  currentY = await drawInfoGrid(page, doc, vouchersInfo, currentY);

  drawLine(page, currentY);

  const firstPageCapacity = getCodesPerPage(currentY, page.getHeight());
  const additionalPageStartY = page.getHeight() - 50;
  const additionalPageCapacity = getCodesPerPage(additionalPageStartY, page.getHeight());
  const totalPages = calculateTotalPages(codes.length, firstPageCapacity, additionalPageCapacity);

  // First batch of codes
  const firstPageCodeCount = firstPageCapacity;
  const firstPageCodes = codes.slice(0, firstPageCodeCount);

  if (firstPageCodes.length > 0) await drawCodesList(page, doc, firstPageCodes, currentY, 0);

  await drawFooter(page, doc, pageNum, totalPages);

  // Additional pages
  let remainingCodes = codes.slice(firstPageCodeCount);
  let processedCount = firstPageCodeCount;

  while (remainingCodes.length > 0) {
    pageNum++;
    page = doc.addPage(PageSizes.A4);

    const codesThisPage = additionalPageCapacity;
    const pageCodes = remainingCodes.slice(0, codesThisPage);

    await drawCodesList(page, doc, pageCodes, additionalPageStartY, processedCount);
    await drawFooter(page, doc, pageNum, totalPages);

    remainingCodes = remainingCodes.slice(codesThisPage);
    processedCount += pageCodes.length;
  }

  // Generate clean filename
  const timestamp = DateTime.now().toFormat("yyyy LLL dd");
  const filename = `vouchers-${vouchersInfo.type.toLowerCase()}${
    vouchersInfo.organizationName ? `-${vouchersInfo.organizationName.replaceAll(" ", "-")}` : ""
  }-${timestamp.replaceAll(" ", "-")}.pdf`;

  // Download
  const dataUri = await doc.saveAsBase64({ dataUri: true });
  const link = document.createElement("a");
  link.href = dataUri;
  link.download = filename;
  link.click();

  console.log(`Generated ${filename} - ${codes.length} codes across ${totalPages} pages`);
}

const getCreditVoucherExpiryDate = () => {
  const date = DateTime.now();
  return date.plus({ year: 1 }).toJSDate();
};
